import { zodResolver } from "@hookform/resolvers/zod";
import { t } from "@lingui/macro";
import { defaultEducation, educationSchema } from "@reactive-resume/schema";
import {
  DateField,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  RichInput,
} from "@reactive-resume/ui";
import dayjs from "dayjs";
import "dayjs/locale/fr";
import localizedFormat from "dayjs/plugin/localizedFormat";
import { useEffect, useRef, useState, useCallback } from "react";
import { useForm } from "react-hook-form";
import type { z } from "zod";

import { AiActions } from "@/client/components/ai-actions";

import { SectionDialog } from "../sections/shared/section-dialog";
import { URLInput } from "../sections/shared/url-input";

dayjs.extend(localizedFormat);
dayjs.locale("fr");

const formSchema = educationSchema;

type FormValues = z.infer<typeof formSchema>;

const formatRange = (start: string, end: string, isPresent: boolean): string => {
  if (!start) return "";
  const s = dayjs(start, "YYYY-MM").isValid() ? dayjs(start, "YYYY-MM").format("MMMM YYYY") : start;
  if (isPresent) return `${s} – Aujourd'hui`;
  if (!end) return s;
  const e = dayjs(end, "YYYY-MM").isValid() ? dayjs(end, "YYYY-MM").format("MMMM YYYY") : end;
  return `${s} – ${e}`;
};

const parseLegacyDate = (
  dateStr: string,
): { startDate: string; endDate: string; isPresent: boolean } => {
  if (!dateStr) return { startDate: "", endDate: "", isPresent: false };

  const normalized = dateStr.replace(/\s*–\s*/, " - ").replace(/\s+to\s+/i, " - ");
  const parts = normalized.split(" - ");
  const rawStart = (parts[0] ?? "").trim();
  const rawEnd = (parts[1] ?? "").trim().replace(/\s*[,.]?\s*$/, "");

  const isPresent = !rawEnd || /^(present|aujourd|current|maintenant)/i.test(rawEnd);

  const tryParseMonth = (val: string): string => {
    if (!val) return "";
    const m = dayjs(val, "YYYY-MM", true);
    if (m.isValid()) return m.format("YYYY-MM");
    const m2 = dayjs(val, "MMMM YYYY").locale("fr");
    if (m2.isValid()) return m2.format("YYYY-MM");
    const m3 = dayjs(val, "MMM YYYY").locale("fr");
    if (m3.isValid()) return m3.format("YYYY-MM");
    const m4 = dayjs(val, "MMMM YYYY");
    if (m4.isValid()) return m4.format("YYYY-MM");
    const m5 = dayjs(val, "MMM YYYY");
    if (m5.isValid()) return m5.format("YYYY-MM");
    const y = dayjs(val, "YYYY", true);
    if (y.isValid()) return y.format("YYYY") + "-01";
    return "";
  };

  return {
    startDate: tryParseMonth(rawStart),
    endDate: isPresent ? "" : tryParseMonth(rawEnd),
    isPresent,
  };
};

export const EducationDialog = () => {
  const form = useForm<FormValues>({
    defaultValues: defaultEducation,
    resolver: zodResolver(formSchema),
  });

  const startDate = form.watch("startDate" as any) ?? "";
  const endDate = form.watch("endDate" as any) ?? "";
  const isPresent = form.watch("isPresent" as any) ?? false;
  const dateVal = form.watch("date" as any) ?? "";
  const migratedRef = useRef(false);
  const [dateError, setDateError] = useState(false);
  const handleDateError = useCallback((err: boolean) => setDateError(err), []);

  useEffect(() => {
    const dateValue = formatRange(startDate, endDate, isPresent);
    form.setValue("date" as any, dateValue);
  }, [startDate, endDate, isPresent, form]);

  useEffect(() => {
    if (migratedRef.current) return;
    if (dateVal && !startDate && !endDate) {
      migratedRef.current = true;
      const parsed = parseLegacyDate(dateVal);
      form.setValue("startDate" as any, parsed.startDate);
      form.setValue("endDate" as any, parsed.endDate);
      form.setValue("isPresent" as any, parsed.isPresent);
    }
  }, [dateVal, startDate, endDate, form]);

  return (
    <SectionDialog<FormValues>
      id="education"
      form={form}
      defaultValues={defaultEducation}
      disabled={dateError}
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <FormField
          name="institution"
          control={form.control}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Établissement</FormLabel>
              <FormControl>
                <Input {...field} placeholder="Ex : Université de Lyon" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          name="studyType"
          control={form.control}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Type de diplôme</FormLabel>
              <FormControl>
                <Input {...field} placeholder="Ex : CAP, BTS, Licence…" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          name="area"
          control={form.control}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Domaine d'études</FormLabel>
              <FormControl>
                <Input {...field} placeholder="Ex : Boulangerie, Infirmier…" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          name="score"
          control={form.control}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Mention / Résultat</FormLabel>
              <FormControl>
                <Input {...field} placeholder="Ex : Mention Bien, 14/20" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="sm:col-span-2">
          <DateField
            startDate={startDate}
            endDate={endDate}
            isPresent={isPresent}
            onStartDateChange={(val) => form.setValue("startDate" as any, val)}
            onEndDateChange={(val) => form.setValue("endDate" as any, val)}
            onPresentChange={(val) => form.setValue("isPresent" as any, val)}
            onError={handleDateError}
          />
        </div>

        <FormField
          name="url"
          control={form.control}
          render={({ field }) => (
            <FormItem className="sm:col-span-2">
              <FormLabel>Site web</FormLabel>
              <FormControl>
                <URLInput {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          name="summary"
          control={form.control}
          render={({ field }) => (
            <FormItem className="sm:col-span-2">
              <FormLabel>Description</FormLabel>
              <FormControl>
                <RichInput
                  {...field}
                  content={field.value}
                  footer={(editor) => (
                    <AiActions
                      value={editor.getText()}
                      onChange={(value) => {
                        editor.commands.setContent(value, true);
                        field.onChange(value);
                      }}
                    />
                  )}
                  onChange={(value) => {
                    field.onChange(value);
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </SectionDialog>
  );
};
